import {
  Alert,
  Box,
  Container,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCallback, useRef, useState } from "react"
import { FiFile, FiFileText, FiImage, FiUpload, FiX } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"

export interface FileUploadProps {
  onUploadComplete?: (fileInfo: unknown) => void
  acceptedFileTypes?: string[]
  maxFileSize?: number // in MB
  uploadType?: string
}

interface UploadingFile {
  file: File
  id: string
  progress: number
  status: "uploading" | "completed" | "error"
  error?: string
}

const MAX_FILE_SIZE_MB = 10
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]

const getFileIcon = (fileType: string) => {
  if (fileType.startsWith("image/")) return FiImage
  if (fileType === "application/pdf" || fileType.includes("document"))
    return FiFileText
  return FiFile
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
}

export const FileUpload = ({
  onUploadComplete,
  acceptedFileTypes = ACCEPTED_FILE_TYPES,
  maxFileSize = MAX_FILE_SIZE_MB,
  uploadType = "attachment",
}: FileUploadProps) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useCustomToast()

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      uploadType,
    }: { file: File; uploadType: string }) => {
      const response = await api.files.uploadFileApiV1FilesUploadPost({
        formData: { file, upload_type: uploadType },
      })
      return response.data
    },
    onSuccess: (data, { file }) => {
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.file === file ? { ...f, status: "completed", progress: 100 } : f,
        ),
      )
      showSuccessToast(`File "${file.name}" uploaded successfully`)
      onUploadComplete?.(data)
      queryClient.invalidateQueries({ queryKey: ["files"] })

      // Remove completed file after a delay
      setTimeout(() => {
        setUploadingFiles((prev) => prev.filter((f) => f.file !== file))
      }, 2000)
    },
    onError: (error: Error, { file }) => {
      const errorMessage = error?.message || "Upload failed"
      setUploadingFiles((prev) =>
        prev.map((f) =>
          f.file === file ? { ...f, status: "error", error: errorMessage } : f,
        ),
      )
      showErrorToast(`Failed to upload "${file.name}": ${errorMessage}`)
    },
  })

  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      return `File type "${file.type}" is not supported`
    }
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size exceeds ${maxFileSize}MB limit`
    }
    return null
  }

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const validFiles: File[] = []

      fileArray.forEach((file) => {
        const error = validateFile(file)
        if (error) {
          showErrorToast(error)
          return
        }
        validFiles.push(file)
      })

      if (validFiles.length === 0) return

      // Add files to uploading state
      const newUploadingFiles = validFiles.map((file) => ({
        file,
        id: `${file.name}-${Date.now()}`,
        progress: 0,
        status: "uploading" as const,
      }))

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles])

      // Start uploads
      validFiles.forEach((file) => {
        uploadMutation.mutate({ file, uploadType })
      })
    },
    [
      uploadMutation,
      uploadType,
      maxFileSize,
      acceptedFileTypes,
      showErrorToast,
    ],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const files = e.dataTransfer.files
      if (files.length > 0) {
        handleFiles(files)
      }
    },
    [handleFiles],
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFiles(files)
      }
      // Reset input value to allow selecting the same file again
      e.target.value = ""
    },
    [handleFiles],
  )

  const removeUploadingFile = useCallback((fileId: string) => {
    setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId))
  }, [])

  return (
    <Container maxW="full" p={0}>
      <Stack gap={4}>
        {/* Upload Zone */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          border="2px dashed"
          borderColor={isDragOver ? "blue.400" : "gray.300"}
          borderRadius="lg"
          p={8}
          textAlign="center"
          cursor="pointer"
          bg={isDragOver ? "blue.50" : "gray.50"}
          transition="all 0.2s"
          _hover={{
            borderColor: "blue.400",
            bg: "blue.50",
          }}
        >
          <Stack align="center" gap={3}>
            <FiUpload size={32} color={isDragOver ? "blue" : "gray"} />
            <Stack align="center" gap={1}>
              <Text fontSize="lg" fontWeight="medium">
                {isDragOver
                  ? "Drop files here"
                  : "Click to upload or drag and drop"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                Maximum file size: {maxFileSize}MB
              </Text>
              <Text fontSize="xs" color="gray.500">
                Supported formats:{" "}
                {acceptedFileTypes
                  .map((type) => type.split("/")[1]?.toUpperCase())
                  .join(", ")}
              </Text>
            </Stack>
          </Stack>

          <Input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedFileTypes.join(",")}
            onChange={handleFileInputChange}
            display="none"
          />
        </Box>

        {/* Uploading Files */}
        {uploadingFiles.length > 0 && (
          <Stack gap={3}>
            <Text fontSize="sm" fontWeight="medium" color="gray.700">
              Uploading Files
            </Text>
            {uploadingFiles.map((uploadingFile) => {
              const FileIcon = getFileIcon(uploadingFile.file.type)
              return (
                <Box
                  key={uploadingFile.id}
                  p={3}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="white"
                >
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <FileIcon size={16} />
                      <Stack gap={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {uploadingFile.file.name}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatFileSize(uploadingFile.file.size)}
                        </Text>
                      </Stack>
                    </HStack>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeUploadingFile(uploadingFile.id)}
                      disabled={uploadingFile.status === "uploading"}
                    >
                      <FiX />
                    </Button>
                  </HStack>

                  {uploadingFile.status === "uploading" && (
                    <Stack gap={1}>
                      <Box
                        bg="gray.200"
                        h={2}
                        borderRadius="sm"
                        position="relative"
                        overflow="hidden"
                      >
                        <Box
                          bg="blue.400"
                          h="full"
                          w={`${uploadingFile.progress}%`}
                          transition="width 0.2s"
                        />
                      </Box>
                      <Text fontSize="xs" color="gray.500">
                        Uploading... {uploadingFile.progress}%
                      </Text>
                    </Stack>
                  )}

                  {uploadingFile.status === "completed" && (
                    <Alert.Root status="success" size="sm">
                      <Alert.Indicator />
                      <Alert.Title>Upload completed!</Alert.Title>
                    </Alert.Root>
                  )}

                  {uploadingFile.status === "error" && (
                    <Alert.Root status="error" size="sm">
                      <Alert.Indicator />
                      <Alert.Title>Upload failed</Alert.Title>
                      <Alert.Description>
                        {uploadingFile.error}
                      </Alert.Description>
                    </Alert.Root>
                  )}
                </Box>
              )
            })}
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

export default FileUpload
